import { createContext, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { NotificationFrequency } from '../components/SearchPage/hooks/useSavedSearch/useSavedSearch.typed';

type SavedSearchModalProviderProps = {
  children: ReactNode;
};

type SavedSearchModalContextValue = {
  isSavedSearchModalOpen: boolean;
  setIsSavedSearchModalOpen: (open: boolean) => void;
  isInstantAlertAvailable: boolean;
  setIsInstantAlertAvailable: (available: boolean) => void;
  isSearchSaved: boolean;
  setIsSearchSaved: (saved: boolean) => void;
  selectedNotificationFrequency?: NotificationFrequency;
  setSelectedNotificationFrequency: (frequency: NotificationFrequency) => void;
};

const SavedSearchModalContext = createContext<SavedSearchModalContextValue>({
  isSavedSearchModalOpen: false,
  setIsSavedSearchModalOpen: () => null,
  isInstantAlertAvailable: false,
  setIsInstantAlertAvailable: () => null,
  isSearchSaved: false,
  setIsSearchSaved: () => null,
  selectedNotificationFrequency: undefined,
  setSelectedNotificationFrequency: () => null,
});

const SavedSearchModalProvider = ({
  children,
}: SavedSearchModalProviderProps) => {
  const [isSavedSearchModalOpen, setIsSavedSearchModalOpen] =
    useState<boolean>(false);
  const [isInstantAlertAvailable, setIsInstantAlertAvailable] =
    useState<boolean>(false);
  const [isSearchSaved, setIsSearchSaved] = useState<boolean>(false);
  const [selectedNotificationFrequency, setSelectedNotificationFrequency] =
    useState<NotificationFrequency | undefined>();

  const value = useMemo(
    () => ({
      isSavedSearchModalOpen,
      setIsSavedSearchModalOpen,
      isInstantAlertAvailable,
      setIsInstantAlertAvailable,
      isSearchSaved,
      setIsSearchSaved,
      selectedNotificationFrequency,
      setSelectedNotificationFrequency,
    }),
    [
      isSavedSearchModalOpen,
      isInstantAlertAvailable,
      isSearchSaved,
      selectedNotificationFrequency,
    ],
  );

  return (
    <SavedSearchModalContext.Provider value={value}>
      {children}
    </SavedSearchModalContext.Provider>
  );
};

const useSavedSearchModalState = () => useContext(SavedSearchModalContext);

export { SavedSearchModalProvider, useSavedSearchModalState };
